import { useState, type FormEvent } from 'react';
import { useMutation } from '@tanstack/react-query';
import { ShieldCheck, ArrowRight, RefreshCw } from 'lucide-react';
import { Button, FormSection } from '../../ui';
import { mfaApi } from '../../services/mfaApi';
import { MfaCodeInput } from './MfaCodeInput';
import { QrCodeDisplay } from './QrCodeDisplay';
import { RecoveryCodesDisplay } from './RecoveryCodesDisplay';
import './MfaSetupWizard.css';

type WizardStep = 'intro' | 'scan' | 'verify' | 'codes';

interface MfaSetupWizardProps {
  // Called when the user completes setup AND acknowledges the recovery codes.
  // Parent typically reloads MFA status and dismisses the wizard.
  onComplete?: () => void;
  onCancel?: () => void;
}

/**
 * Step-by-step TOTP enrollment:
 * intro → scan (QR) → verify (6-digit code) → codes (one-time recovery codes)
 *
 * The wizard owns the API state; the parent is informed only on completion.
 * If the parent wants to track progress for analytics, we can add a step
 * callback later.
 */
export function MfaSetupWizard({ onComplete, onCancel }: MfaSetupWizardProps) {
  const [step, setStep] = useState<WizardStep>('intro');
  const [secret, setSecret] = useState<string | null>(null);
  const [qrCodeSvg, setQrCodeSvg] = useState<string | null>(null);
  const [uri, setUri] = useState<string | null>(null);
  const [verifyCode, setVerifyCode] = useState('');
  const [recoveryCodes, setRecoveryCodes] = useState<string[]>([]);
  const [recoveryWarning, setRecoveryWarning] = useState<string | undefined>();
  const [verifyError, setVerifyError] = useState<string | null>(null);

  const setupMutation = useMutation({
    mutationFn: () => mfaApi.setupTotp(),
    onSuccess: response => {
      if (response.success && response.data) {
        setSecret(response.data.secret);
        setQrCodeSvg(response.data.qr_code_svg);
        setUri(response.data.uri);
        setStep('scan');
      }
    },
  });

  const verifyMutation = useMutation({
    mutationFn: (code: string) => mfaApi.verifyTotpSetup(code),
    onSuccess: response => {
      if (response.success && response.data) {
        setRecoveryCodes(response.data.recovery_codes);
        setRecoveryWarning(response.data.recovery_codes_warning);
        setStep('codes');
        setVerifyError(null);
      } else {
        setVerifyError(response.error || 'Invalid code. Try again.');
      }
    },
    onError: (err: Error) => setVerifyError(err.message),
  });

  const handleStart = () => {
    setVerifyError(null);
    setVerifyCode('');
    setupMutation.mutate();
  };

  const handleVerify = (e: FormEvent) => {
    e.preventDefault();
    if (verifyCode.length !== 6) {
      setVerifyError('Enter all 6 digits.');
      return;
    }
    verifyMutation.mutate(verifyCode);
  };

  return (
    <FormSection
      title={
        <span className="mfa-setup-wizard__title">
          <ShieldCheck size={18} aria-hidden="true" /> Set up authenticator app
        </span>
      }
      description="Use Google Authenticator, 1Password, Authy, or any TOTP-compatible app."
    >
      <div className="mfa-setup-wizard" data-step={step} data-testid="mfa-setup-wizard">
        {step === 'intro' && (
          <div className="mfa-setup-wizard__intro">
            <p>
              You&apos;ll be asked for a 6-digit code from your authenticator app every time you
              sign in. We&apos;ll also give you one-time recovery codes in case you lose access to
              the app.
            </p>
            <div className="mfa-setup-wizard__actions">
              {onCancel && (
                <Button type="button" variant="secondary" onClick={onCancel}>
                  Cancel
                </Button>
              )}
              <Button type="button" onClick={handleStart} loading={setupMutation.isPending}>
                Start setup <ArrowRight size={16} aria-hidden="true" />
              </Button>
            </div>
            {setupMutation.isError && (
              <p className="mfa-setup-wizard__error">
                {(setupMutation.error as Error).message || 'Failed to start MFA setup'}
              </p>
            )}
          </div>
        )}

        {step === 'scan' && (
          <div className="mfa-setup-wizard__scan">
            <ol className="mfa-setup-wizard__steps">
              <li>Open your authenticator app.</li>
              <li>Scan this QR code (or copy the manual key).</li>
              <li>
                Click <strong>Continue</strong> and enter the 6-digit code shown in your app.
              </li>
            </ol>
            <QrCodeDisplay
              svg={qrCodeSvg ?? undefined}
              secret={secret ?? undefined}
              uri={uri ?? undefined}
            />
            <div className="mfa-setup-wizard__actions">
              {onCancel && (
                <Button type="button" variant="secondary" onClick={onCancel}>
                  Cancel
                </Button>
              )}
              <Button type="button" onClick={() => setStep('verify')}>
                Continue <ArrowRight size={16} aria-hidden="true" />
              </Button>
            </div>
          </div>
        )}

        {step === 'verify' && (
          <form onSubmit={handleVerify} className="mfa-setup-wizard__verify">
            <p>Enter the 6-digit code from your authenticator app.</p>
            <MfaCodeInput
              value={verifyCode}
              onChange={code => {
                setVerifyError(null);
                setVerifyCode(code);
              }}
              autoFocus
              disabled={verifyMutation.isPending}
            />
            {verifyError && <p className="mfa-setup-wizard__error">{verifyError}</p>}
            <div className="mfa-setup-wizard__actions">
              <Button
                type="button"
                variant="secondary"
                onClick={() => setStep('scan')}
                disabled={verifyMutation.isPending}
              >
                <RefreshCw size={16} aria-hidden="true" /> Back to QR code
              </Button>
              <Button
                type="submit"
                loading={verifyMutation.isPending}
                disabled={verifyMutation.isPending}
              >
                Verify and enable MFA
              </Button>
            </div>
          </form>
        )}

        {step === 'codes' && (
          <div className="mfa-setup-wizard__codes">
            <p>
              <strong>You&apos;re almost done.</strong> Save these recovery codes — each can be used
              once if you lose access to your authenticator app.
            </p>
            <RecoveryCodesDisplay
              codes={recoveryCodes}
              warning={recoveryWarning}
              onAcknowledge={onComplete}
            />
          </div>
        )}
      </div>
    </FormSection>
  );
}
